import { FeatureCollection, Geometry } from '@turf/turf'
import { Feature, MultiPolygon, Polygon } from 'geojson'
import CircleMaker from './CircleMaker'
import geoutils from './helper/geo'
import Image from './image'
import LineStringMaker from './LineStringMaker'
import MultiPolygonMaker, { MultiPolygonOptions } from './MultiPolygonMaker'
import { buildSvgOverlay } from './svg2png'
import { RasterTileSuccessResult, TileConfig, TilesetService } from './TilesetService'
import { ShapeMaker, TilesetConfig } from './types'

type StaticMapsOptions = {
  tileLayers?: TilesetConfig[]
  width: number
  height: number
  paddingX?: number
  paddingY?: number
  tileSize?: number
  tileRequestTimeout?: number
  tileRequestHeader?: { [key: string]: string }
  tileRequestLimit?: number
  reverseY?: boolean
  zoomRange?: {
    min?: number
    max?: number
  }
  maxZoom?: number // alias for zoomRange.max
}

export class StaticMaps {
  tileLayers?: TilesetConfig[]
  width: number
  height: number
  paddingX?: number
  paddingY?: number
  padding: [number, number]
  tileSize: number
  tileRequestTimeout?: number
  tileRequestHeader?: { [key: string]: string }
  tileRequestLimit?: number
  reverseY?: boolean
  zoomRange: {
    min: number
    max: number
  }
  maxZoom?: number // alias for zoomRange.max

  shapeMakers: ShapeMaker[]
  masks: any[]

  bounds: any[]

  // # fields that get set when map is rendered
  center: number[]
  centerX: number
  centerY: number
  zoom: number

  image?: Image

  constructor(options: StaticMapsOptions) {
    this.tileLayers = options.tileLayers

    this.width = options.width
    this.height = options.height
    this.paddingX = options.paddingX || 0
    this.paddingY = options.paddingY || 0
    this.padding = [this.paddingX, this.paddingY]
    this.tileSize = options.tileSize || 256
    this.tileRequestTimeout = options.tileRequestTimeout
    this.tileRequestHeader = options.tileRequestHeader
    this.tileRequestLimit = Number.isFinite(options.tileRequestLimit)
      ? Number(options.tileRequestLimit)
      : 2
    this.reverseY = options.reverseY || false
    const zoomRange = options.zoomRange || {}
    this.zoomRange = {
      min: zoomRange.min || 1,
      max: options.maxZoom || zoomRange.max || 17, // maxZoom
    }

    this.masks = []
    this.shapeMakers = []

    // this.text = []
    this.bounds = []

    // # fields that get set when map is rendered
    this.center = []
    this.centerX = 0
    this.centerY = 0
    this.zoom = 0
  }

  addShape(shape: Feature | FeatureCollection | Geometry, options: any) {}

  /** @deprecated use addShape() instead */
  addMultiPolygon(options: MultiPolygonOptions) {
    this.shapeMakers.push(new MultiPolygonMaker(options))
  }

  addMask(geometry: Polygon | MultiPolygon | FeatureCollection) {
    const options = {
      fill: '#ffffffff', // draw the mask interior
      color: '#00000000', // no outline
    }

    switch (geometry.type) {
      case 'FeatureCollection':
        ;(geometry as FeatureCollection).features.forEach(feature => {
          this.addMask((feature as Feature<Polygon | MultiPolygon>).geometry)
        })
        break

      case 'Polygon':
        this.masks.push(
          new MultiPolygonMaker({
            coords: geometry.coordinates,
            ...options,
          })
        )
        break

      case 'MultiPolygon':
        geometry.coordinates.forEach(coords => {
          this.masks.push(
            new MultiPolygonMaker({
              coords,
              ...options,
            })
          )
        })
        break

      default:
        console.warn('can not mask with geometry', geometry)
    }
  }

  /**
   * Render static map with all map features that were added to map before
   */
  async render(centerOrBbox: [number, number] | [number, number, number, number], zoom?: number) {
    this.center = centerOrBbox

    // @ts-ignore one of these should be there
    this.zoom = zoom || this.getExtentZoom(22)

    const maxZoom = this.zoomRange?.max
    if (maxZoom && this.zoom > maxZoom) this.zoom = maxZoom

    if (centerOrBbox?.length === 2) {
      this.centerX = geoutils.lonToX(centerOrBbox[0], this.zoom)
      this.centerY = geoutils.latToY(centerOrBbox[1], this.zoom)
    } else {
      // # get extent of all lines
      const extent = this.determineExtent()

      // # calculate center point of map
      const centerLon = (extent[0] + extent[2]) / 2
      const centerLat = (extent[1] + extent[3]) / 2

      this.centerX = geoutils.lonToX(centerLon, this.zoom)
      this.centerY = geoutils.latToY(centerLat, this.zoom)
    }

    if (!this.shapeMakers.length && !centerOrBbox) {
      throw new Error(
        'Unable to determine extents. Add render box or shapes to map before rendering.'
      )
    }

    this.image = new Image({ width: this.width, height: this.height })

    if (this.tileLayers?.length) {
      for (const layer of this.tileLayers) {
        await this.drawTileLayer(layer, this.zoom)
      }
    }

    await this.drawFeatures()

    await this.applyMasks()

    return this.image.image
  }

  /**
   * calculate common extent of all current map features
   */
  determineExtent() {
    const extents = []

    // Add bbox to extent
    if (this.center && this.center.length >= 4) extents.push(this.center)

    // add bounds to extent
    if (this.bounds.length) {
      this.bounds.forEach(bound => extents.push(bound.extent()))
    }

    this.shapeMakers.forEach(shape => {
      extents.push(shape.extent())
    })

    // @TODO: Add marker to extent
    // for (let i = 0; i < this.markers.length; i++) {
    //   const marker = this.markers[i]
    //   const e = [marker.coord[0], marker.coord[1]]
    //
    //   if (!zoom) {
    //     extents.push([marker.coord[0], marker.coord[1], marker.coord[0], marker.coord[1]])
    //     continue
    //   }
    //
    //   // # consider dimension of marker
    //   const ePx = marker.extentPx()
    //   const x = geoutils.lonToX(e[0], zoom)
    //   const y = geoutils.latToY(e[1], zoom)
    //
    //   extents.push([
    //     geoutils.xToLon(x - parseFloat(ePx[0]) / this.tileSize, zoom),
    //     geoutils.yToLat(y + parseFloat(ePx[1]) / this.tileSize, zoom),
    //     geoutils.xToLon(x + parseFloat(ePx[2]) / this.tileSize, zoom),
    //     geoutils.yToLat(y - parseFloat(ePx[3]) / this.tileSize, zoom),
    //   ])
    // }

    return [
      Math.min(...extents.map(e => e[0])),
      Math.min(...extents.map(e => e[1])),
      Math.max(...extents.map(e => e[2])),
      Math.max(...extents.map(e => e[3])),
    ]
  }

  /**
   * calculate the best zoom level for given extent
   */
  getExtentZoom(forceMaxZoom: number) {
    if (!this.zoomRange) return 0
    for (let z = forceMaxZoom || this.zoomRange.max; z >= this.zoomRange.min; z--) {
      const extent = this.determineExtent()
      const width = (geoutils.lonToX(extent[2], z) - geoutils.lonToX(extent[0], z)) * this.tileSize
      if (width > this.width - this.padding[0] * 2) continue

      const height = (geoutils.latToY(extent[1], z) - geoutils.latToY(extent[3], z)) * this.tileSize
      if (height > this.height - this.padding[1] * 2) continue

      return z
    }
    return this.zoomRange?.min
  }

  /**
   * transform tile number to pixel on image canvas
   */
  xToPx(x: number) {
    const px = (x - this.centerX) * this.tileSize + this.width / 2
    return Number(Math.round(px))
  }
  /**
   * transform pixel on image canvas to tile number
   */
  pxToX(px: number) {
    const x = (px - this.width / 2) / this.tileSize + this.centerX
    return x
  }

  /**
   * transform tile number to pixel on image canvas
   */
  yToPx(y: number) {
    const px = (y - this.centerY) * this.tileSize + this.height / 2
    return Number(Math.round(px))
  }
  /**
   * pixel on image canvas to transform tile number
   */
  pxToY(px: number) {
    const y = (px - this.height / 2) / this.tileSize + this.centerY
    return y
  }

  drawVectorTileLayer(config: any, tiles: any[]) {
    const stroke = config.stroke || '#000000'
    const width = config.width || 1
    const fill = config.fill || '#00000000'

    for (const result of tiles) {
      for (const tileFeature of result.tile.features) {
        const feature = config.callback?.(tileFeature) || tileFeature

        if (!feature.geometry.coordinates.length) break

        switch (feature.geometry.type) {
          case 'Polygon':
            this.shapeMakers.push(
              new MultiPolygonMaker({
                coords: [feature.geometry.coordinates],
                stroke,
                width,
                fill,
              })
            )
            break

          case 'MultiPolygon':
            this.shapeMakers.push(
              new MultiPolygonMaker({
                coords: feature.geometry.coordinates,
                stroke,
                width,
                fill,
              })
            )
            break

          case 'LineString':
            this.shapeMakers.push(
              new LineStringMaker({
                coords: feature.geometry.coordinates,
                stroke,
                width,
              })
            )
            break

          case 'MultiLineString':
            feature.geometry.coordinates.forEach((coords: number[][]) => {
              this.shapeMakers.push(
                new LineStringMaker({
                  coords: coords,
                  stroke,
                  width,
                })
              )
            })
            break

          case 'Point':
            this.shapeMakers.push(
              new CircleMaker({
                center: feature.geometry.coordinates,
                radius: config.width,
                stroke,
                width,
              })
            )
            break

          default:
            console.warn('unable to render vector tile feature', feature)
        }
      }
    }
  }

  async drawTileLayer(config: any, zoom: number) {
    if (!config || !config.tileUrl || !this.image) {
      // Early return if we shouldn't draw a base layer
      console.warn('invalid tile layer config', config)
      return this.image?.draw([])
    }
    const xMin = Math.floor(this.centerX - (0.5 * this.width) / this.tileSize)
    const yMin = Math.floor(this.centerY - (0.5 * this.height) / this.tileSize)
    const xMax = Math.ceil(this.centerX + (0.5 * this.width) / this.tileSize)
    const yMax = Math.ceil(this.centerY + (0.5 * this.height) / this.tileSize)

    const tileConfigs: TileConfig[] = []

    if (!xMin || !yMin || !xMax || !yMax) {
      console.warn('no available area to render tile layer')
      return this.image.draw([])
    }

    for (let x = xMin; x < xMax; x++) {
      for (let y = yMin; y < yMax; y++) {
        // # x and y may have crossed the date line
        const maxTile = 2 ** zoom
        const tileX = (x + maxTile) % maxTile
        let tileY = (y + maxTile) % maxTile
        if (this.reverseY) tileY = (1 << zoom) - tileY - 1

        tileConfigs.push({
          address: { x: tileX, y: tileY, z: zoom },
          // url: tileUrl,
          box: [this.xToPx(x), this.yToPx(y), this.xToPx(x + 1), this.yToPx(y + 1)],
        })
      }
    }

    const tiles = await TilesetService.getTiles(config, tileConfigs)

    if (config.type === 'vector') {
      this.drawVectorTileLayer(config, tiles)
    } else {
      const goodTiles = tiles.filter(v => v.success) as RasterTileSuccessResult[]
      return this.image.draw(goodTiles.map(v => v.tile))
    }
  }

  /**
   *  Draw all features to the basemap
   */
  async drawFeatures() {
    if (!this.shapeMakers.length || !this.image) return

    const features = []

    for (const maker of this.shapeMakers) {
      features.push(maker.toSVG(this))
    }

    const overlay = await buildSvgOverlay(features, this.image)

    if (overlay) {
      const ctx = this.image.image?.getContext('2d')
      ctx?.drawImage(overlay, 0, 0)
    }
  }

  async applyMasks() {
    if (!this.masks.length || !this.image?.image) return

    const imageCtx = this.image.image.getContext('2d')
    if (!imageCtx) return

    const { width, height } = this.image.image

    const maskFeatures = []

    for (const feature of this.masks) {
      maskFeatures.push(feature.toSVG(this))
    }

    const maskCanvas = await buildSvgOverlay(maskFeatures, { width, height })
    if (!maskCanvas) return

    imageCtx.globalCompositeOperation = 'destination-in'
    imageCtx.drawImage(maskCanvas, 0, 0, width, height)
    imageCtx.globalCompositeOperation = 'source-over'
  }

  mapToRasterCoords(lng: number, lat: number) {
    const tileX = geoutils.lonToX(lng, this.zoom)
    const tileY = geoutils.latToY(lat, this.zoom)

    return { x: this.xToPx(tileX), y: this.yToPx(tileY) }
  }

  rasterToMapCoords(x: number, y: number) {
    const tileX = this.pxToX(x)
    const tileY = this.pxToY(y)

    return { lng: geoutils.xToLon(tileX, this.zoom), lat: geoutils.yToLat(tileY, this.zoom) }
  }
}
